// LockerWallEditor — Schrank-Wand-Editor (Phase 3 + 4)
// - Schränke platzieren / verschieben / skalieren
// - Bulk-Grid-Generator
// - Status-Farbe: frei=grün, belegt=orange, deaktiviert=grau, reserviert=gelb
// - Klick auf Schrank → AssignmentDialog
// - Mode 'edit' für Editoren / 'view' für Mitarbeiter
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Plus, ZoomIn, ZoomOut, Maximize2, Trash2,
  Grid3x3, MousePointer2, Hand, Power, PowerOff,
  Edit3, Palette, Copy, Armchair, RotateCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { schrankApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Separator } from '../ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { DraggableRect, clientToSvg } from './DraggableRect';
import { useSvgViewport } from './useSvgViewport';
import { AssignmentDialog } from './AssignmentDialog';
import { SchrankDoor } from './SchrankDoor';
import { SchrankGroupRenderer } from './SchrankWall';
import { ContainerDetailModal } from './ContainerDetailModal';
import { MoebelSymbol, MOEBEL_TYPEN } from './MoebelSymbol';

const SCHRANK_TYP_LABELS = {
  PERSOENLICH: 'Persönlich',
  WASCHRAUM: 'Waschraum',
  ESSENSRAUM: 'Essensraum',
  GEMEINSCHAFT: 'Gemeinschaft',
  CUSTOM: 'Eigene',
};

const STATUS_COLOR = {
  FREI:         '#10b981', // grün
  BELEGT:       '#f97316', // orange
  DEAKTIVIERT:  '#94a3b8', // grau
  RESERVIERT:   '#eab308', // gelb
};
const STATUS_LABEL = {
  FREI: 'Frei', BELEGT: 'Belegt', DEAKTIVIERT: 'Deaktiviert', RESERVIERT: 'Reserviert',
};

export function LockerWallEditor({ room, buildingName, floorName, canEdit, canAssign, onBack }) {
  const [schraenke, setSchraenke] = useState([]);
  const [moebel, setMoebel] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMoebelId, setSelectedMoebelId] = useState(null);
  const [mode, setMode] = useState('select');  // 'select' | 'draw' | 'moebel'
  const [drawing, setDrawing] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({ spalten: 3, reihen: 2, name_prefix: 'F' });
  const [isConverting, setIsConverting] = useState(false);
  const [assignDialog, setAssignDialog] = useState({ open: false, schrank: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteMoebelTarget, setDeleteMoebelTarget] = useState(null);
  const [containerModal, setContainerModal] = useState({ open: false, container: null });

  // Raum-Innenraum-Coords: nutze Raum-Größe als Canvas
  const roomW = Math.round(room?.geometry?.width || 400);
  const roomH = Math.round(room?.geometry?.height || 300);
  const viewport = useSvgViewport({
    contentWidth: roomW,
    contentHeight: roomH,
    enableDragPan: mode !== 'draw',
  });

  const load = useCallback(async () => {
    if (!room) return;
    setIsLoading(true);
    try {
      const [resSchraenke, resMoebel] = await Promise.all([
        schrankApi.listSchraenke({ room_id: room.id, top_level: true }),
        schrankApi.listMoebel(room.id),
      ]);
      setSchraenke(resSchraenke.data);
      setMoebel(resMoebel.data);
    } catch (e) {
      console.error(e);
      toast.error('Schränke / Möbel konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [room]);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => schraenke.find(s => s.id === selectedId) || null,
    [schraenke, selectedId]
  );
  const selectedMoebel = useMemo(
    () => moebel.find(m => m.id === selectedMoebelId) || null,
    [moebel, selectedMoebelId]
  );

  // Canvas-Click-Down: drawing oder deselect
  const onCanvasPointerDown = useCallback((e) => {
    if (viewport.spaceDown || e.button !== 0) return;
    if (mode === 'draw' && canEdit) {
      const p = clientToSvg(viewport.svgRef.current, e.clientX, e.clientY);
      setDrawing({ startX: p.x, startY: p.y, x: p.x, y: p.y, w: 0, h: 0 });
    } else {
      setSelectedId(null);
      setSelectedMoebelId(null);
    }
  }, [mode, canEdit, viewport.spaceDown, viewport.svgRef]);

  useEffect(() => {
    if (!drawing) return;
    const move = (e) => {
      const p = clientToSvg(viewport.svgRef.current, e.clientX, e.clientY);
      setDrawing((d) => d ? {
        ...d,
        x: Math.min(d.startX, p.x),
        y: Math.min(d.startY, p.y),
        w: Math.abs(p.x - d.startX),
        h: Math.abs(p.y - d.startY),
      } : null);
    };
    const up = async () => {
      const d = drawing;
      setDrawing(null);
      if (!d || d.w < 8 || d.h < 8) return;
      try {
        const res = await schrankApi.createSchrank(room.id, {
          name: `S-${schraenke.length + 1}`,
          typ: 'PERSOENLICH',
          geometry: {
            x: Math.round(d.x), y: Math.round(d.y),
            width: Math.round(d.w), height: Math.round(d.h),
            rotation: 0, color: '#94a3b8',
          },
        });
        setSchraenke((prev) => [...prev, res.data]);
        setSelectedId(res.data.id);
        setMode('select');
        toast.success('Schrank angelegt');
      } catch (e) {
        toast.error('Anlegen fehlgeschlagen');
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [drawing, room, schraenke.length, viewport.svgRef]);

  const handleGeometryChange = (id, geo) => {
    setSchraenke((prev) => prev.map(s => s.id === id ? { ...s, geometry: geo } : s));
  };
  const handleGeometryCommit = async (id, geo) => {
    try { await schrankApi.updateSchrank(id, { geometry: geo }); }
    catch { toast.error('Speichern fehlgeschlagen'); }
  };

  const saveProps = async (patch) => {
    if (!selected) return;
    try {
      const res = await schrankApi.updateSchrank(selected.id, patch);
      setSchraenke((prev) => prev.map(s => s.id === selected.id ? res.data : s));
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selected) return;
    if (selected.aktuelle_assignment_id && newStatus !== 'BELEGT') {
      toast.error('Erst aktuelle Zuweisung beenden, bevor Status geändert wird');
      return;
    }
    await saveProps({ status: newStatus });
    toast.success(`Status: ${STATUS_LABEL[newStatus]}`);
  };

  const handleDuplicate = async () => {
    if (!selected) return;
    // Nächste freie Nummer im Namen finden (z.B. "A-01" → "A-02", "A-99" → "A-100")
    const match = selected.name.match(/^(.*?)(\d+)$/);
    let newName = `${selected.name} Kopie`;
    if (match) {
      const [, prefix, numStr] = match;
      const pad = numStr.length;
      let n = parseInt(numStr, 10) + 1;
      const existingNames = new Set(schraenke.map(s => s.name));
      while (existingNames.has(`${prefix}${String(n).padStart(pad, '0')}`)) n += 1;
      newName = `${prefix}${String(n).padStart(pad, '0')}`;
    }
    // Geometry: kopieren mit kleinem Offset, damit Original nicht verdeckt
    const g = selected.geometry;
    const offset = 8;
    const newGeo = {
      ...g,
      x: g.x + g.width + offset,
      y: g.y,
    };
    try {
      const res = await schrankApi.createSchrank(room.id, {
        name: newName,
        typ: selected.typ,
        custom_typ: selected.custom_typ,
        tuer_richtung: selected.tuer_richtung || 'SUED',
        notiz: selected.notiz || null,
        geometry: newGeo,
        // wand_id NICHT übernehmen — Kopie ist Einzelschrank, kann manuell gruppiert werden
      });
      setSchraenke((prev) => [...prev, res.data]);
      setSelectedId(res.data.id);
      toast.success(`"${newName}" dupliziert`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplizieren fehlgeschlagen');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await schrankApi.deleteSchrank(deleteTarget.id);
      setSchraenke((prev) => prev.filter(s => s.id !== deleteTarget.id));
      if (selectedId === deleteTarget.id) setSelectedId(null);
      toast.success(`"${deleteTarget.name}" gelöscht`);
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setDeleteTarget(null);
    }
  };

  // ---------- Möbel-Handler ----------
  const addMoebel = async (moebelDef) => {
    if (!canEdit) return;
    const w = moebelDef.defaultW;
    const h = moebelDef.defaultH;
    // Mittig im Raum platzieren, in den sichtbaren Bereich gequetscht
    const x = Math.max(0, Math.min(roomW - w, Math.round((roomW - w) / 2)));
    const y = Math.max(0, Math.min(roomH - h, Math.round((roomH - h) / 2)));
    try {
      const res = await schrankApi.createMoebel(room.id, {
        typ: moebelDef.typ,
        name: null,
        geometry: { x, y, width: w, height: h, rotation: 0 },
      });
      setMoebel((prev) => [...prev, res.data]);
      setSelectedMoebelId(res.data.id);
      setSelectedId(null);
      setMode('select');
      toast.success(`${moebelDef.label} platziert`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${moebelDef.label} konnte nicht angelegt werden`);
    }
  };

  const handleMoebelGeometryChange = (id, geo) => {
    setMoebel((prev) => prev.map(m => m.id === id ? { ...m, geometry: geo } : m));
  };
  const handleMoebelGeometryCommit = async (id, geo) => {
    try { await schrankApi.updateMoebel(id, { geometry: geo }); }
    catch { toast.error('Möbel-Position speichern fehlgeschlagen'); }
  };

  const saveMoebelProps = async (patch) => {
    if (!selectedMoebel) return;
    try {
      const res = await schrankApi.updateMoebel(selectedMoebel.id, patch);
      setMoebel((prev) => prev.map(m => m.id === selectedMoebel.id ? res.data : m));
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const handleMoebelRotate = async () => {
    if (!selectedMoebel) return;
    const g = selectedMoebel.geometry || {};
    const rot = ((g.rotation || 0) + 90) % 360;
    await saveMoebelProps({ geometry: { ...g, rotation: rot } });
  };

  const onMoebelClick = (m) => {
    setSelectedMoebelId(m.id);
    setSelectedId(null);
  };

  const confirmDeleteMoebel = async () => {
    if (!deleteMoebelTarget) return;
    try {
      await schrankApi.deleteMoebel(deleteMoebelTarget.id);
      setMoebel((prev) => prev.filter(m => m.id !== deleteMoebelTarget.id));
      if (selectedMoebelId === deleteMoebelTarget.id) setSelectedMoebelId(null);
      toast.success('Möbel gelöscht');
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setDeleteMoebelTarget(null);
    }
  };

  // ---------- Container-Konvertierung ----------
  const handleConvertToContainer = async () => {
    if (!selected || selected.ist_container) return;
    setIsConverting(true);
    try {
      const res = await schrankApi.convertToContainer(selected.id, {
        spalten: Number(convertForm.spalten) || 1,
        reihen: Number(convertForm.reihen) || 1,
        abstand_x: 2,
        abstand_y: 2,
        name_prefix: convertForm.name_prefix || 'F',
      });
      // res.data[0] = Container, rest = Fächer
      const container = res.data[0];
      setSchraenke((prev) => prev.map(s => s.id === selected.id ? container : s));
      setSelectedId(container.id);
      setConvertOpen(false);
      toast.success(`Schrank in Container mit ${res.data.length - 1} Fächern umgewandelt`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Konvertierung fehlgeschlagen');
    } finally {
      setIsConverting(false);
    }
  };

  const onSchrankClick = (s) => {
    setSelectedId(s.id);
    setSelectedMoebelId(null);
    // Bei Container: direkt Detail-Modal öffnen (Doppelklick optional)
    if (s.ist_container) {
      setContainerModal({ open: true, container: s });
    }
  };
  const onSchrankDoubleClick = (s) => {
    if (s.ist_container) {
      setContainerModal({ open: true, container: s });
    } else if (canAssign) {
      setAssignDialog({ open: true, schrank: s });
    }
  };

  const statusCounts = useMemo(() => {
    const c = { FREI: 0, BELEGT: 0, DEAKTIVIERT: 0, RESERVIERT: 0 };
    schraenke.forEach(s => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [schraenke]);

  return (
    <div className="space-y-3" data-testid="locker-wall-editor">
      {/* Toolbar */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <Separator orientation="vertical" className="h-7" />
          <div className="flex items-center gap-1">
            <span className="font-semibold text-sm">Raum: {room?.name}</span>
            <Badge variant="secondary" className="ml-1">{schraenke.length} Schränke</Badge>
          </div>
          <Separator orientation="vertical" className="h-7" />
          {canEdit && (
            <>
              <Button variant={mode === 'select' ? 'default' : 'outline'} size="sm"
                      onClick={() => setMode('select')}>
                <MousePointer2 className="w-4 h-4 mr-1" /> Auswählen
              </Button>
              <Button variant={mode === 'draw' ? 'default' : 'outline'} size="sm"
                      onClick={() => setMode('draw')}>
                <Plus className="w-4 h-4 mr-1" /> Schrank
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="moebel-menu-btn">
                    <Armchair className="w-4 h-4 mr-1" /> Möbel
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
                  {MOEBEL_TYPEN.map((mt) => (
                    <DropdownMenuItem
                      key={mt.typ}
                      onSelect={() => addMoebel(mt)}
                      data-testid={`add-moebel-${mt.typ}`}
                    >
                      {mt.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Separator orientation="vertical" className="h-7" />
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => viewport.zoomBy(0.8)}>
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => viewport.zoomBy(1.25)}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs font-mono text-muted-foreground min-w-[3rem] text-center">
            {viewport.zoomPercent}%
          </span>
          <Button variant="outline" size="sm" onClick={viewport.resetView}>
            <Maximize2 className="w-4 h-4" />
          </Button>
          {/* Status-Legende */}
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden md:inline">
              Snap: an Nachbarn rastern · Shift = frei
            </span>
            <div className="flex items-center gap-2 text-xs">
              {Object.entries(STATUS_COLOR).map(([s, c]) => (
                <div key={s} className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: c }} />
                  <span className="text-muted-foreground">{STATUS_LABEL[s]}</span>
                  <Badge variant="secondary" className="h-4 text-[10px]">{statusCounts[s] || 0}</Badge>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-[1fr_320px] gap-3">
        {/* Canvas */}
        <Card>
          <CardContent className="p-0 overflow-hidden">
            {isLoading ? (
              <Skeleton className="w-full" style={{ aspectRatio: `${roomW} / ${roomH}` }} />
            ) : (
              <div
                ref={viewport.containerRef}
                className={cn(
                  'relative bg-slate-50 dark:bg-slate-900 select-none',
                  viewport.isPanning ? 'cursor-grabbing' : viewport.spaceDown ? 'cursor-grab' : '',
                  mode === 'draw' && canEdit && !viewport.spaceDown && 'cursor-crosshair'
                )}
                style={{ aspectRatio: `${roomW} / ${roomH}`, maxHeight: '70vh' }}
              >
                <svg
                  ref={viewport.svgRef}
                  viewBox={viewport.viewBoxString}
                  width="100%"
                  height="100%"
                  onWheel={viewport.handleWheel}
                  onPointerDown={(e) => {
                    viewport.handlePointerDown(e);
                    if (!viewport.spaceDown && e.button === 0) onCanvasPointerDown(e);
                  }}
                  style={{ display: 'block', touchAction: 'none' }}
                >
                  {/* Raum-Hintergrund */}
                  <rect x={0} y={0} width={roomW} height={roomH}
                        fill={room?.geometry?.color || '#f1f5f9'} />
                  <rect x={0} y={0} width={roomW} height={roomH}
                        fill="none" stroke="#475569" strokeWidth={2} />

                  {/* Möbel (hinter den Schränken, zur Orientierung) */}
                  {moebel.map((m) => (
                    <MoebelSymbol
                      key={`mb-${m.id}`}
                      moebel={m}
                      selected={m.id === selectedMoebelId}
                      onClick={() => onMoebelClick(m)}
                    />
                  ))}
                  {/* Drag-Overlay für Möbel im Edit-Modus */}
                  {canEdit && mode === 'select' && moebel.map((m) => (
                    <DraggableRect
                      key={`mb-drag-${m.id}`}
                      svgRef={viewport.svgRef}
                      geometry={m.geometry}
                      selected={m.id === selectedMoebelId}
                      canEdit={true}
                      onSelect={() => onMoebelClick(m)}
                      onChange={(g) => handleMoebelGeometryChange(m.id, g)}
                      onCommit={(g) => handleMoebelGeometryCommit(m.id, g)}
                      fillOverride="rgba(0,0,0,0)"
                      strokeOverride={m.id === selectedMoebelId ? '#dc2626' : 'rgba(0,0,0,0)'}
                      testId={`moebel-drag-${m.id}`}
                    />
                  ))}

                  {/* Schränke gruppiert nach wand_id - mit Drag-Overlay im Edit-Modus */}
                  <SchrankGroupRenderer
                    schraenke={schraenke}
                    selectedId={selectedId}
                    onSchrankClick={onSchrankClick}
                  />
                  {/* Drag-Overlay - transparent über jedem Schrank im Edit-Modus */}
                  {canEdit && mode === 'select' && schraenke.map((s) => {
                    // Snap-Targets: alle anderen Schränke + Raum-Außenkanten
                    const snapTargets = schraenke
                      .filter(other => other.id !== s.id)
                      .map(other => other.geometry);
                    // Auch die Raum-Außenkanten als Targets (für „an Wand snappen")
                    snapTargets.push({ x: 0, y: 0, width: roomW, height: roomH });
                    return (
                      <DraggableRect
                        key={`drag-${s.id}`}
                        svgRef={viewport.svgRef}
                        geometry={s.geometry}
                        selected={s.id === selectedId}
                        canEdit={true}
                        onSelect={() => onSchrankClick(s)}
                        onChange={(g) => handleGeometryChange(s.id, g)}
                        onCommit={(g) => handleGeometryCommit(s.id, g)}
                        onDoubleClick={() => onSchrankDoubleClick(s)}
                        fillOverride="rgba(0,0,0,0)"
                        strokeOverride={s.id === selectedId ? '#dc2626' : 'rgba(0,0,0,0)'}
                        snapTargets={snapTargets}
                        testId={`schrank-${s.id}`}
                      />
                    );
                  })}

                  {drawing && drawing.w > 0 && drawing.h > 0 && (
                    <rect
                      x={drawing.x} y={drawing.y}
                      width={drawing.w} height={drawing.h}
                      fill="#dc2626" fillOpacity={0.15}
                      stroke="#dc2626" strokeWidth={1.5}
                      strokeDasharray="4 3"
                    />
                  )}
                </svg>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Properties Panel */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {selectedMoebel
                ? `Möbel: ${MOEBEL_TYPEN.find(t => t.typ === selectedMoebel.typ)?.label || selectedMoebel.typ}`
                : selected ? `Schrank ${selected.name}` : 'Kein Objekt ausgewählt'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedMoebel ? (
              <>
                <div>
                  <Label>Typ</Label>
                  <Select value={selectedMoebel.typ}
                          onValueChange={(v) => saveMoebelProps({ typ: v })}>
                    <SelectTrigger data-testid="moebel-typ-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MOEBEL_TYPEN.map((mt) => (
                        <SelectItem key={mt.typ} value={mt.typ}>{mt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {canEdit && (
                  <div>
                    <Label>Bezeichnung (optional)</Label>
                    <Input
                      value={selectedMoebel.name || ''}
                      onChange={(e) => setMoebel(prev =>
                        prev.map(m => m.id === selectedMoebel.id ? { ...m, name: e.target.value } : m))}
                      onBlur={(e) => saveMoebelProps({ name: e.target.value || null })}
                      placeholder="z.B. Esstisch, Bett 1"
                      data-testid="moebel-name-input"
                    />
                  </div>
                )}
                {canEdit && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm" onClick={handleMoebelRotate}
                            data-testid="moebel-rotate-btn">
                      <RotateCw className="w-4 h-4 mr-1" /> Drehen 90°
                    </Button>
                    <Button variant="outline" size="sm"
                            onClick={() => setDeleteMoebelTarget(selectedMoebel)}
                            className="text-destructive hover:text-destructive"
                            data-testid="moebel-delete-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Löschen
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Möbel dienen ausschließlich der Orientierung im Raumplan.
                </p>
              </>
            ) : !selected ? (
              <p className="text-sm text-muted-foreground">
                {canEdit && mode === 'draw'
                  ? 'Ziehe ein Rechteck im Raum, um einen Schrank zu erstellen.'
                  : 'Klicke einen Schrank an, um Details zu sehen.'}
              </p>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge style={{
                    backgroundColor: STATUS_COLOR[selected.status],
                    color: '#fff'
                  }}>
                    {STATUS_LABEL[selected.status]}
                  </Badge>
                  <Badge variant="outline">
                    {SCHRANK_TYP_LABELS[selected.typ]}
                    {selected.custom_typ ? `: ${selected.custom_typ}` : ''}
                  </Badge>
                </div>

                {selected.mitarbeiter_name && (
                  <div className="p-2 rounded-md border bg-orange-50 dark:bg-orange-950/20">
                    <p className="text-xs text-muted-foreground">Belegt von</p>
                    <p className="font-semibold text-sm">{selected.mitarbeiter_name}</p>
                  </div>
                )}
                {selected.abteilung_name && !selected.mitarbeiter_name && (
                  <div className="p-2 rounded-md border bg-orange-50 dark:bg-orange-950/20">
                    <p className="text-xs text-muted-foreground">Belegt von Abteilung</p>
                    <p className="font-semibold text-sm">{selected.abteilung_name}</p>
                  </div>
                )}

                {canEdit && (
                  <>
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={selected.name}
                        onChange={(e) => setSchraenke(prev =>
                          prev.map(s => s.id === selected.id ? { ...s, name: e.target.value } : s))}
                        onBlur={(e) => saveProps({ name: e.target.value })}
                        data-testid="schrank-name-input"
                      />
                    </div>
                    <div>
                      <Label>Typ</Label>
                      <Select value={selected.typ}
                              onValueChange={(v) => saveProps({ typ: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(SCHRANK_TYP_LABELS).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selected.typ === 'CUSTOM' && (
                      <div>
                        <Label>Eigene Typ-Bezeichnung</Label>
                        <Input
                          value={selected.custom_typ || ''}
                          onChange={(e) => setSchraenke(prev =>
                            prev.map(s => s.id === selected.id ? { ...s, custom_typ: e.target.value } : s))}
                          onBlur={(e) => saveProps({ custom_typ: e.target.value || null })}
                        />
                      </div>
                    )}
                    <div>
                      <Label>Türrichtung</Label>
                      <Select value={selected.tuer_richtung || 'SUED'}
                              onValueChange={(v) => saveProps({ tuer_richtung: v })}>
                        <SelectTrigger data-testid="schrank-tuer-richtung">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SUED">↓ Süd (unten)</SelectItem>
                          <SelectItem value="NORD">↑ Nord (oben)</SelectItem>
                          <SelectItem value="OST">→ Ost (rechts)</SelectItem>
                          <SelectItem value="WEST">← West (links)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        In welche Richtung sich die Türen öffnen.
                      </p>
                    </div>

                    {/* Raster-Konvertierung */}
                    {!selected.ist_container ? (
                      <div className="rounded-md border bg-slate-50 dark:bg-slate-900/40 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold flex items-center gap-1.5">
                            <Grid3x3 className="w-4 h-4" /> Mit Raster (Schrankwand)
                          </p>
                        </div>
                        {!convertOpen ? (
                          <>
                            <p className="text-xs text-muted-foreground">
                              Diesen Schrank in eine Schrankwand mit mehreren Fächern umwandeln.
                              Der Schrank bleibt im Raum als ein Block sichtbar — die Fächer
                              erscheinen nur in der Detail-Ansicht beim Anklicken.
                            </p>
                            <Button variant="outline" size="sm" className="w-full"
                                    onClick={() => setConvertOpen(true)}
                                    data-testid="convert-to-container-btn">
                              <Grid3x3 className="w-4 h-4 mr-1.5" /> In Schrankwand umwandeln
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Spalten</Label>
                                <Input type="number" min={1} max={50}
                                       value={convertForm.spalten}
                                       onChange={(e) => setConvertForm(f => ({ ...f, spalten: e.target.value }))}
                                       data-testid="convert-spalten" />
                              </div>
                              <div>
                                <Label className="text-xs">Reihen</Label>
                                <Input type="number" min={1} max={20}
                                       value={convertForm.reihen}
                                       onChange={(e) => setConvertForm(f => ({ ...f, reihen: e.target.value }))}
                                       data-testid="convert-reihen" />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Fach-Namens-Präfix</Label>
                              <Input value={convertForm.name_prefix}
                                     onChange={(e) => setConvertForm(f => ({ ...f, name_prefix: e.target.value }))}
                                     placeholder="z.B. F, A, K" />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Es werden <strong>{(Number(convertForm.spalten) || 0) * (Number(convertForm.reihen) || 0)}</strong> Fächer
                              im Inneren angelegt. Eine bestehende Zuweisung wird beendet.
                            </p>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" className="flex-1"
                                      onClick={() => setConvertOpen(false)}
                                      disabled={isConverting}>
                                Abbrechen
                              </Button>
                              <Button size="sm" className="flex-1"
                                      onClick={handleConvertToContainer}
                                      disabled={isConverting}
                                      data-testid="convert-apply-btn">
                                {isConverting ? '…' : 'Anwenden'}
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-1">
                        <p className="text-sm font-semibold flex items-center gap-1.5">
                          <Grid3x3 className="w-4 h-4" /> Schrankwand
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {selected.geometry?.spalten || '?'} × {selected.geometry?.reihen || '?'} Fächer.
                          Klicke den Schrank im Plan an, um die Fächer zu öffnen und einzeln zuzuweisen.
                        </p>
                      </div>
                    )}
                  </>
                )}

                <div className="flex flex-col gap-2">
                  {canAssign && (
                    <Button onClick={() => setAssignDialog({ open: true, schrank: selected })}
                            data-testid="open-assign-btn">
                      <Edit3 className="w-4 h-4 mr-2" />
                      {selected.aktuelle_assignment_id ? 'Zuweisung ändern' : 'Zuweisen'}
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="outline"
                      onClick={handleDuplicate}
                      data-testid="duplicate-schrank-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" /> Duplizieren
                    </Button>
                  )}
                  {canEdit && (
                    <div className="grid grid-cols-2 gap-2">
                      {selected.status !== 'DEAKTIVIERT' ? (
                        <Button variant="outline" size="sm"
                                onClick={() => handleStatusChange('DEAKTIVIERT')}>
                          <PowerOff className="w-4 h-4 mr-1" /> Deaktivieren
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm"
                                onClick={() => handleStatusChange('FREI')}>
                          <Power className="w-4 h-4 mr-1" /> Aktivieren
                        </Button>
                      )}
                      <Button variant="outline" size="sm"
                              onClick={() => setDeleteTarget(selected)}
                              className="text-destructive hover:text-destructive">
                        <Trash2 className="w-4 h-4 mr-1" /> Löschen
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <AssignmentDialog
        open={assignDialog.open}
        onOpenChange={(open) => setAssignDialog((s) => ({ ...s, open }))}
        schrank={assignDialog.schrank}
        canManage={canAssign}
        onChanged={load}
      />

      <ContainerDetailModal
        open={containerModal.open}
        onOpenChange={(open) => setContainerModal((s) => ({ ...s, open }))}
        container={containerModal.container}
        canAssign={canAssign}
        onChanged={load}
        buildingName={buildingName}
        floorName={floorName}
        roomName={room?.name}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Schrank löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> wird mit seiner Zuweisungs-Historie gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteMoebelTarget} onOpenChange={(open) => !open && setDeleteMoebelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Möbel löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieses Möbel-Symbol wird aus dem Raumplan entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMoebel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default LockerWallEditor;
